import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { sendPasswordResetEmail } from 'firebase/auth';
import {
  approveRegistrationResponseSchema,
  registrationResponseSchema,
  registrationsResponseSchema,
  roleSchema,
  unitsResponseSchema,
  type Registration,
  type Role,
} from '@unit/shared';
import { auth } from '../../lib/firebase.js';
import { useApi } from '../../lib/useApi.js';
import { apiPost } from '../../lib/api.js';
import { useAction } from '../../lib/useAction.js';
import { ApiError } from '../../components/ApiError.js';
import { ActionError } from '../../components/ActionError.js';
import { formatDate } from '../issuance/FormsPage.js';
import { unitOptions } from '../units/unitOptions.js';
import { TitlePicker } from '../users/TitlePicker.js';

const input = 'rounded-lg border border-slate-300 px-3 py-1.5 text-sm text-slate-900';
const primary =
  'rounded-lg bg-slate-900 px-3 py-1.5 text-sm font-medium text-white transition hover:bg-slate-700 disabled:opacity-40';
const danger = 'rounded-lg bg-red-700 px-3 py-1.5 text-sm font-medium text-white hover:bg-red-800 disabled:opacity-40';

/**
 * Pending self-registrations (story 33), admin only. Approving places the
 * person on a unit with roles and creates the account through the same
 * path as "add soldier"; the set-your-password email is opt-in, as
 * everywhere else. Rejecting keeps the row with a note.
 */
export function RegistrationsPage() {
  const { t, i18n } = useTranslation();
  const [status, setStatus] = useState<'pending' | 'approved' | 'rejected'>('pending');
  const list = useApi(`/registrations?status=${status}`, registrationsResponseSchema);
  const units = useApi('/units', unitsResponseSchema);

  if (list.kind === 'error') return <ApiError code={list.code} requestId={list.requestId} />;
  const options = units.kind === 'ready' ? unitOptions(units.data.units, (name) => t('users.directOnPlatoon', { platoon: name })) : [];

  return (
    <section className="rounded-2xl bg-white p-5 shadow-sm ring-1 ring-slate-200" data-testid="registrations-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold text-slate-900">{t('registrations.title')}</h2>
          <p className="mt-1 text-sm text-slate-500">{t('registrations.intro')}</p>
        </div>
        <div className="flex gap-1 text-sm">
          {(['pending', 'approved', 'rejected'] as const).map((s) => (
            <button
              key={s}
              type="button"
              onClick={() => setStatus(s)}
              className={`rounded-lg px-3 py-1 ring-1 ${status === s ? 'bg-slate-900 text-white ring-slate-900' : 'text-slate-700 ring-slate-300 hover:bg-slate-100'}`}
              data-testid={`registrations-${s}`}
            >
              {t(`registrations.status.${s}`)}
            </button>
          ))}
        </div>
      </div>

      {list.kind === 'loading' && <p className="mt-3 text-sm text-slate-500">{t('home.loading')}</p>}
      {list.kind === 'ready' && list.data.registrations.length === 0 && (
        <p className="mt-3 text-sm text-slate-500" data-testid="registrations-empty">{t('registrations.empty')}</p>
      )}
      {list.kind === 'ready' && list.data.registrations.length > 0 && (
        <ul className="mt-3 divide-y divide-slate-100">
          {list.data.registrations.map((r) => (
            <RegistrationRow key={r.id} r={r} options={options} language={i18n.language} onChanged={list.reload} />
          ))}
        </ul>
      )}
    </section>
  );
}

function RegistrationRow({
  r,
  options,
  language,
  onChanged,
}: {
  r: Registration;
  options: { id: string; label: string }[];
  language: string;
  onChanged: () => void;
}) {
  const { t } = useTranslation();
  const [open, setOpen] = useState<'approve' | 'reject' | null>(null);
  const [unitId, setUnitId] = useState('');
  const [roles, setRoles] = useState<Role[]>(['soldier']);
  const [rank, setRank] = useState('');
  const [title, setTitle] = useState('');
  const [sendEmail, setSendEmail] = useState(false);
  const [note, setNote] = useState('');
  const [emailResult, setEmailResult] = useState<'sent' | 'failed' | null>(null);
  const approve = useAction(() =>
    apiPost(`/registrations/${r.id}/approve`, approveRegistrationResponseSchema, {
      unitId,
      roles,
      rank: rank.trim() || null,
      title: title.trim() || null,
    }),
  );
  const reject = useAction(() => apiPost(`/registrations/${r.id}/reject`, registrationResponseSchema, { note: note.trim() || null }));

  const doApprove = async () => {
    const result = await approve.run();
    if (!result) return;
    if (sendEmail) {
      try {
        await sendPasswordResetEmail(auth, result.user.email ?? r.email);
        setEmailResult('sent');
      } catch {
        setEmailResult('failed');
      }
    }
    onChanged();
  };

  return (
    <li className="py-3" data-testid="registration-row" data-id={r.id} data-status={r.status}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <span>
          <span className="font-medium text-slate-900">{r.firstName} {r.lastName}</span>
          <span className="latin-inline ms-2 text-sm text-slate-600">{r.personalNumber}</span>
          <span className="latin-inline ms-2 text-sm text-slate-600">{r.email}</span>
          <span className="latin-inline ms-2 text-sm text-slate-600">{r.phone}</span>
        </span>
        <span className="flex items-center gap-2 text-xs text-slate-500">
          {r.submittedAt && <span className="latin-inline">{formatDate(r.submittedAt, language)}</span>}
          {r.status === 'pending' && (
            <>
              <button type="button" onClick={() => setOpen(open === 'approve' ? null : 'approve')} className="rounded px-2 py-0.5 text-xs text-slate-700 ring-1 ring-slate-300 hover:bg-slate-100" data-testid="registration-approve-open">
                {t('registrations.approve')}
              </button>
              <button type="button" onClick={() => setOpen(open === 'reject' ? null : 'reject')} className="rounded px-2 py-0.5 text-xs text-red-800 ring-1 ring-red-200 hover:bg-red-50" data-testid="registration-reject-open">
                {t('registrations.reject')}
              </button>
            </>
          )}
          {r.status === 'approved' && r.createdUid && (
            <Link to={`/org/_/soldier/${r.createdUid}`} className="underline-offset-4 hover:underline">{t('registrations.openFile')}</Link>
          )}
          {r.status === 'rejected' && r.decisionNote && <span>{r.decisionNote}</span>}
        </span>
      </div>

      {open === 'approve' && (
        <div className="mt-2 grid gap-2 rounded-xl bg-slate-50 p-3 ring-1 ring-slate-200 sm:grid-cols-2" data-testid="registration-approve-panel">
          <label className="text-xs text-slate-600">
            {t('users.unit')}
            <select className={`${input} mt-1 block w-full`} value={unitId} onChange={(e) => setUnitId(e.target.value)} data-testid="registration-unit">
              <option value="">—</option>
              {options.map((o) => (
                <option key={o.id} value={o.id}>{o.label}</option>
              ))}
            </select>
          </label>
          <label className="text-xs text-slate-600">
            {t('users.rank')}
            <input className={`${input} mt-1 block w-full`} value={rank} onChange={(e) => setRank(e.target.value)} />
          </label>
          <label className="text-xs text-slate-600">
            {t('org.columns.title')}
            <TitlePicker className={`${input} mt-1 block w-full`} value={title} onChange={setTitle} testId="registration-title" />
          </label>
          <fieldset className="text-xs text-slate-600 sm:col-span-2">
            <legend>{t('org.columns.roles')}</legend>
            <div className="mt-1 flex flex-wrap gap-3">
              {roleSchema.options.map((role) => (
                <label key={role} className="flex items-center gap-1.5">
                  <input
                    type="checkbox"
                    checked={roles.includes(role)}
                    onChange={(e) => setRoles((cur) => (e.target.checked ? [...cur, role] : cur.filter((x) => x !== role)))}
                    data-testid={`registration-role-${role}`}
                  />
                  {t(`roles.${role}`)}
                </label>
              ))}
            </div>
          </fieldset>
          <label className="flex items-center gap-2 text-xs text-slate-700 sm:col-span-2">
            <input type="checkbox" checked={sendEmail} onChange={(e) => setSendEmail(e.target.checked)} data-testid="registration-send-email" />
            {t('users.sendInviteEmail')}
          </label>
          <div className="flex items-center gap-2 sm:col-span-2">
            <button type="button" onClick={() => void doApprove()} disabled={approve.busy || !unitId || roles.length === 0} className={primary} data-testid="registration-approve">
              {t('registrations.approveConfirm')}
            </button>
            <ActionError error={approve.error} />
          </div>
        </div>
      )}
      {open === 'reject' && (
        <div className="mt-2 flex flex-wrap items-end gap-2 rounded-xl bg-red-50 p-3 ring-1 ring-red-200" data-testid="registration-reject-panel">
          <label className="grow text-xs text-slate-600">
            {t('registrations.note')}
            <input className={`${input} mt-1 block w-full`} value={note} onChange={(e) => setNote(e.target.value)} data-testid="registration-note" />
          </label>
          <button type="button" onClick={() => void reject.run().then((ok) => ok && onChanged())} disabled={reject.busy} className={danger} data-testid="registration-reject">
            {t('registrations.rejectConfirm')}
          </button>
          <ActionError error={reject.error} />
        </div>
      )}
      {emailResult && (
        <p className="mt-1 text-xs text-slate-600" data-testid={`registration-email-${emailResult}`}>
          {emailResult === 'sent' ? t('registrations.emailSent') : t('registrations.emailFailed')}
        </p>
      )}
    </li>
  );
}
